// ============================================================
// Multi-Story News Show Composer — Type Definitions
// ============================================================

import type {
  TemplateColorPalette,
  TemplateFontStyle,
  UploadedAsset,
  AnimationPreset,
  OutputSettings,
} from '../types';

// ─── Custom Layer (user-added Text / Image / Video / Animation) ──────

export type CustomLayerType = 'text' | 'image' | 'video' | 'animation';

export interface CustomLayer {
  id: string;
  type: CustomLayerType;
  label: string;
  // Position & size (logical 1920×1080 px)
  x: number;
  y: number;
  w: number;
  h: number;
  zIndex: number;
  opacity: number;
  visible: boolean;
  // Text
  text?: string;
  fontSize?: number;
  fontFamily?: string;
  color?: string;
  fontWeight?: number;
  bgColor?: string;
  textAlign?: 'left' | 'center' | 'right';
  // Image / Video
  src?: string;
  objectFit?: 'cover' | 'contain' | 'fill';
  // Animation applied to this layer on entrance
  animationType?: 'none' | 'fade-in' | 'slide-left' | 'slide-right' | 'slide-up' | 'slide-down' | 'zoom-in' | 'pulse';
  animationDelay?: number;    // frames
  animationDuration?: number; // frames
}

// ─── Content Layer Transforms (per-layer position / scale / opacity) ─

export type ContentLayerKey =
  | 'primaryMedia'
  | 'secondaryMedia'
  | 'headline'
  | 'subheadline'
  | 'bigNumber'
  | 'quoteText';

export interface ContentLayerTransform {
  dx:      number;   // X offset in 1920×1080 logical pixels (0 = default)
  dy:      number;   // Y offset
  scale:   number;   // 1.0 = default size (used when w/h are 0/unset)
  opacity: number;   // 0–1
  visible: boolean;  // false = hidden
  w?:      number;   // explicit width override in logical px (0 or absent = auto/natural)
  h?:      number;   // explicit height override in logical px (0 or absent = auto/natural)
  zIndex?: number;   // CSS z-index within scene stacking context (0 = auto)
}

export const DEFAULT_CONTENT_LAYER_TRANSFORM: ContentLayerTransform = {
  dx: 0, dy: 0, scale: 1, opacity: 1, visible: true, w: 0, h: 0,
};

export type ContentLayerTransforms = Partial<Record<ContentLayerKey, ContentLayerTransform>>;

// ─── Overlay Color / Image Overrides ─────────────────────────

export interface OverlayColorOverride {
  bgColor?:    string;  // background color override
  textColor?:  string;  // text / ticker text color override
  accentColor?: string; // accent stripe color override
  bgImageUrl?: string;  // background image URL (replaces solid color when set)
}

// ─── Overlay Geometry (position + size of each overlay element) ──

export interface OverlayElementGeometry {
  x: number;        // left offset in 1920×1080 logical space
  y: number;        // top offset in 1920×1080 logical space
  w: number;        // width in logical pixels
  h: number;        // height in logical pixels
  fontSize: number; // font size in logical pixels
  zIndex?: number;  // CSS z-index (default: element-specific tier)
}

export interface OverlayGeometry {
  ticker?:         OverlayElementGeometry;
  topBar?:         OverlayElementGeometry;
  logo?:           OverlayElementGeometry;
  storyCounter?:   OverlayElementGeometry;
  breakingBanner?: OverlayElementGeometry;
}

export const DEFAULT_OVERLAY_GEOMETRY: Required<OverlayGeometry> = {
  ticker:         { x: 0,    y: 1030, w: 1920, h: 50, fontSize: 20 },
  topBar:         { x: 0,    y: 0,    w: 1920, h: 54, fontSize: 15 },
  logo:           { x: 1700, y: 12,   w: 200,  h: 60, fontSize: 14 },
  storyCounter:   { x: 0,    y: 66,   w: 300,  h: 40, fontSize: 18 },
  breakingBanner: { x: 0,    y: 0,    w: 1920, h: 52, fontSize: 20 },
};

// ─── Layout Config (user-editable per-layout defaults) ────────

export type ScreenStyle =
  | 'full-screen'      // L1, L6, L11
  | 'split-lr'         // L2, L3, L4, L8
  | 'split-tb'         // top visual / bottom text
  | 'grid'             // L10
  | 'text-focus'       // L5, L12
  | 'graphic';         // L7

export interface LayoutConfig {
  id: string;                   // 'L1' … 'L12' or custom 'custom-uuid'
  baseLayoutId: string;         // which Remotion layout component to render
  label: string;
  description: string;
  isCustom?: boolean;
  screenStyle: ScreenStyle;
  // Media
  showImage: boolean;
  showVideo: boolean;
  videoMuted: boolean;
  preferVideo: boolean;         // prefer video over image when both available
  // Audio
  voiceoverEnabled: boolean;
  bgMusicEnabled: boolean;
  // Text elements
  showHeadline: boolean;
  showSubheadline: boolean;
  showBigNumber: boolean;
  showQuote: boolean;
  showHighlightWords: boolean;
  // Overlays
  showTicker: boolean;
  showLogo: boolean;
  showTopBar: boolean;
  showStoryCounter: boolean;
  showBreakingBanner: boolean;
  // Timing
  defaultDuration: number;      // seconds
  // Per-layout overlay geometry overrides
  overlayGeometry?: OverlayGeometry;
  // Per-overlay color / image overrides (keyed by overlay name: 'topBar', 'ticker', etc.)
  overlayColors?: Record<string, OverlayColorOverride>;
}

// ─── Scene Layout IDs ────────────────────────────────────────

export type SceneLayoutId =
  | 'L1'   // Full Screen Visual
  | 'L2'   // Visual Left / Text Right
  | 'L3'   // Text Left / Visual Right
  | 'L4'   // Two Visual Split
  | 'L5'   // Full Screen Headline
  | 'L6'   // Full Screen Visual + Bottom Headline
  | 'L7'   // Big Number Graphic
  | 'L8'   // Quote Layout
  | 'L10'  // Multi-image Grid
  | 'L11'  // Video Focus
  | 'L12'  // Text Highlight
  | 'L13'; // Lower Third

export const LAYOUT_LABELS: Record<SceneLayoutId, string> = {
  L1:  'Full Screen Visual',
  L2:  'Visual Left · Text Right',
  L3:  'Text Left · Visual Right',
  L4:  'Two Visual Split',
  L5:  'Full Screen Headline',
  L6:  'Visual + Bottom Headline',
  L7:  'Big Number Graphic',
  L8:  'Quote Layout',
  L10: 'Multi-image Grid',
  L11: 'Video Focus',
  L12: 'Text Highlight',
  L13: 'Lower Third',
};

// ─── Scene Assets ─────────────────────────────────────────────

export interface SceneAssets {
  primary?: UploadedAsset;     // main image or video
  secondary?: UploadedAsset;   // second image for split layouts
  images?: UploadedAsset[];    // for grid (up to 4)
  video?: UploadedAsset;       // dedicated video slot
  personPhoto?: UploadedAsset; // for quote layout
  logo?: UploadedAsset;
}

// ─── Scene Props (passed into Remotion SceneComposition) ─────

export interface SceneProps {
  // Content
  headline: string;
  subheadline?: string;
  bigNumber?: string;        // e.g. "₹5,000 करोड़" for L7
  quoteText?: string;        // for L8
  highlightWords?: string[]; // for L12
  reporterName?: string;
  location?: string;
  date?: string;

  // Layout
  layoutId: SceneLayoutId;

  // Assets
  assets: SceneAssets;

  // Branding / Template styling
  colorPalette: TemplateColorPalette;
  headlineFont: TemplateFontStyle;
  subheadlineFont: TemplateFontStyle;

  // Global overlays (persistent across scenes)
  tickerText?: string;
  showTicker?: boolean;
  tickerSpeed?: number;
  showLogo?: boolean;
  showTopBar?: boolean;
  topBarText?: string;

  // Story counter ("खबर नंबर 3")
  storyCounterText?: string;
  showStoryCounter?: boolean;

  // Breaking news mode
  isBreaking?: boolean;
  breakingLabel?: string;    // e.g. "BREAKING NEWS"

  // Animation
  sceneEnterPreset?: AnimationPreset;
  sceneExitPreset?: AnimationPreset;

  // Audio
  voiceoverUrl?: string;      // story voiceover audio file
  bgMusicUrl?: string;        // show-level background music
  voiceoverVolume?: number;   // 0–1, default 1.0
  bgMusicVolume?: number;     // 0–1, default 0.3
  /**
   * Seconds into the audio files to seek when this scene starts.
   * Keeps voiceover and bgMusic continuous across scene cuts.
   * - voiceover: offset within this story's voiceover file
   * - bgMusic:   offset within the show-wide BGM (= scene startSec)
   */
  audioOffsetSec?: number;

  // Asset visibility & video audio
  videoMuted?: boolean;       // mute the video track in this scene (default false)
  hideImage?: boolean;        // hide all images in this scene (show solid bg instead)
  hideVideo?: boolean;        // hide video in this scene (fall back to image)

  // Overlay geometry (positions/sizes editable per layout)
  overlayGeometry?: OverlayGeometry;

  // Per-overlay color / background image overrides
  overlayColors?: Record<string, OverlayColorOverride>;

  // Per-layer position / scale / opacity overrides
  contentLayerTransforms?: ContentLayerTransforms;

  // User-added custom layers (text, image, video, animation)
  customLayers?: CustomLayer[];

  // Internal
  _renderMode?: 'preview' | 'render'; // set to 'preview' by Player to suppress bgMusic inside composition
  _fps?: number;                       // fps passed through for audioOffsetSec → startFrom conversion
}

// ─── Story (one news item in a show) ─────────────────────────

export interface StoryAsset {
  id: string;
  asset: UploadedAsset;
  role: 'primary' | 'secondary' | 'grid' | 'video' | 'audio' | 'logo' | 'person';
  order: number;
}

export interface Story {
  id: string;
  index: number;           // 1-based position in show
  enabled: boolean;
  isBreaking: boolean;
  title: string;           // internal label
  headline: string;
  subheadline: string;
  tickerText: string;
  script: string;          // voiceover script
  bigNumber?: string;      // prominent stat if any
  quoteText?: string;
  personName?: string;
  location: string;
  reporterName: string;
  category: string;
  assets: StoryAsset[];
  sceneMode: 'auto' | 'manual';
  duration: number;        // total story duration in seconds
  scenes: Scene[];
}

// ─── Scene ───────────────────────────────────────────────────

export interface Scene {
  id: string;
  layoutId: SceneLayoutId;
  duration: number;        // seconds
  startSec: number;        // relative to story start
  props: SceneProps;
  transitionIn?: TransitionPreset;
}

// ─── Transition ───────────────────────────────────────────────

export type TransitionPreset =
  | 'cut'
  | 'fade'
  | 'slide-left'
  | 'slide-right'
  | 'zoom-in'
  | 'wipe-left'
  | 'wipe-right'
  | 'flash';

// ─── Show Template (config-driven show format) ────────────────

export type ShowTemplateId =
  | 'top-10-news'
  | 'top-20-news'
  | 'fatafat-20'
  | 'breaking-bulletin'
  | 'morning-bulletin'
  | 'evening-bulletin'
  | 'business-news'
  | 'sports-bulletin'
  | 'custom-show';

export interface ShowRules {
  maxConsecutiveSameLayout: number;
  transitionDuration: number;         // seconds
  shortTransitionDuration: number;    // for mid-show transitions
  periodicLogoAnimation: boolean;
  logoAnimationInterval: number;      // seconds
  preferBreakingTransition: boolean;
  defaultSceneDuration: number;       // seconds per scene
  scenesPerStory: number;             // auto-gen scene count
}

export interface GlobalOverlayConfig {
  ticker: boolean;
  tickerSpeed: number;
  logo: boolean;
  topBar: boolean;
  lowerThird: boolean;
  storyCounter: boolean;
  counterPattern: string;           // 'खबर नंबर {n}' | 'NEWS #{n:02}'
  breakingBannerOnBreaking: boolean;
}

export interface ShowTemplate {
  id: ShowTemplateId;
  name: string;
  nameHi: string;
  maxStories: number | null;
  allowedLayouts: SceneLayoutId[];
  globalOverlay: GlobalOverlayConfig;
  rules: ShowRules;
  colorPalette: TemplateColorPalette;
  headlineFont: TemplateFontStyle;
  subheadlineFont: TemplateFontStyle;
  introPreset: string;
  outroPreset: string;
  defaultTransition: TransitionPreset;
  midTransition: TransitionPreset;
}

// ─── Show Project ─────────────────────────────────────────────

export interface ShowProject {
  id: string;
  name: string;
  showTemplateId: ShowTemplateId;
  showTemplate: ShowTemplate;
  outputSettings: OutputSettings;
  language: string;
  date: string;
  createdAt: string;
  updatedAt: string;
  stories: Story[];
  masterTimeline?: ShowTimelineItem[];
  // Show-level media
  bgMusic?: UploadedAsset;        // background music track
  introPreset: string;            // e.g. 'buzz24-classic', 'slide-in', 'flash'
  voiceoverVolume: number;        // 0–1
  bgMusicVolume: number;          // 0–1
}

// ─── Master Timeline ──────────────────────────────────────────

export type ShowTimelineItemType =
  | 'intro'
  | 'story-counter'
  | 'scene'
  | 'transition'
  | 'outro';

export interface ShowTimelineItem {
  id: string;
  type: ShowTimelineItemType;
  startSec: number;
  endSec: number;
  storyId?: string;
  storyIndex?: number;
  sceneId?: string;
  sceneProps?: SceneProps;
}
